import React, { useEffect, useState } from "react";
import IncludeSideBar from "../../Components/Sidebar/IncludeSideBar";
import "./AvatarScreen.css";
import useAuth from '../../../src/useAuth';
import { MEDIA_SERVER_URL } from "../../constants/constants";

function AvatarScreen() {
  const { user } = useAuth();
  const hasUploadAvatar = user?.permissions?.Avatar?.['Upload Avatar']?.selected || user?.role === 'superadmin';

  const [avatars, setAvatars] = useState([]);
  const avataApis = require("../../apis/avatar");
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false); // New state for upload loading
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const usersOnCurrentPage = avatars.slice(startIndex, endIndex);
  const totalPages = Math.ceil(avatars.length / itemsPerPage);
  const showPrevButton = currentPage > 1;
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  // Generate pagination buttons
  const paginationButtons = [];
  for (let i = startPage; i <= endPage; i++) {
    paginationButtons.push(
      <button
        key={i}
        onClick={() => handlePageChange(i)}
        disabled={isUploading} // Disable during upload
        className={currentPage === i ? "active" : ""}
      >
        {i}
      </button>
    );
  }
  if (startPage > 1) {
    paginationButtons.unshift(<span key="dots1">...</span>);
    paginationButtons.unshift(
      <button key={1} onClick={() => handlePageChange(1)} disabled={isUploading}>
        1
      </button>
    );
  }
  if (endPage < totalPages) {
    paginationButtons.push(<span key="dots2">...</span>);
    paginationButtons.push(
      <button key={totalPages} onClick={() => handlePageChange(totalPages)} disabled={isUploading}>
        {totalPages}
      </button>
    );
  }
  const showNextButton = currentPage < totalPages;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getAllAvatars = async () => {
    setIsLoading(true);
    try {
      const result = await avataApis.getAllAvatars();
      const userData = result.data;
      const filteredUsers = userData.filter((user) =>
        user.id.toString().includes(searchTerm)
      );
      setAvatars(filteredUsers);
    } catch (error) {
      console.error("Error fetching Avatars:", error);
      alert("Failed to fetch avatars. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAvatar = async (id) => {
    if (window.confirm("Are you sure you want to delete this avatar?")) {
      setIsUploading(true); // Disable buttons during delete
      try {
        await avataApis.deleteAllAvatars(id);
        await getAllAvatars();
        alert("Avatar deleted successfully!");
      } catch (error) {
        console.error("Error deleting avatar:", error);
        alert("Failed to delete avatar. Please try again.");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleAvatarUpload = async (formData) => {
    if (!selectedImage) {
      alert("Please select an image to upload.");
      return;
    }
    setIsUploading(true);
    try {
      const response = await avataApis.addAllAvatars(formData);
      if (response) {
        await getAllAvatars();
        setSelectedImage(null);
        alert("Image uploaded successfully!");
      } else {
        const errorData = await response.json();
        throw new Error(`Upload failed: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error("Avatar upload error:", error);
      alert(`Failed to upload image: ${error.message}`);
      setSelectedImage(null);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    getAllAvatars();
  }, [searchTerm]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
  };

  const handleNoAccess = () => {
    alert('You are not able to access this feature.');
  };

  return (
    <IncludeSideBar>
      <div className="avatar-screen">
        <h1 className="header">List of All Avatars</h1>

        <div className="upload-image">
          {selectedImage && <p>Selected: {selectedImage.name}</p>}
          <label className="file-input-label">
            Choose File
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              disabled={isUploading}
            />
          </label>
          <button
            onClick={() => {
              if (hasUploadAvatar) {
                const formData = new FormData();
                formData.append("image", selectedImage, selectedImage.name);
                handleAvatarUpload(formData);
              } else {
                handleNoAccess();
              }
            }}
            disabled={isUploading || !hasUploadAvatar}
            style={{
              backgroundColor: !hasUploadAvatar ? '#C5C6C7' : '',
              cursor: !hasUploadAvatar || isUploading ? 'not-allowed' : 'pointer',
            }}
          >
            {isUploading ? "Uploading..." : "Upload Image"}
          </button>
        </div>

        <div className="search-container">
          <input
            className="search-input"
            type="text"
            placeholder="Search by ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isUploading}
          />
          <button
            className="search-button"
            onClick={() => setCurrentPage(1)}
            disabled={isUploading}
          >
            Search
          </button>
        </div>

        {isLoading ? (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        ) : (
          <div>
            <div className="avatar-rows">
              {usersOnCurrentPage
                .reduce((rows, item, index) => {
                  if (index % 5 === 0) {
                    rows.push([]);
                  }
                  rows[rows.length - 1].push(item);
                  return rows;
                }, [])
                .map((row, rowIndex) => (
                  <div className="avatar-row" key={rowIndex}>
                    {row.map((item) => (
                      <div className="avatar-card" key={item.id}>
                        <img
                          src={`${MEDIA_SERVER_URL}/${item.avatar_url}`}
                          alt={`Avatar ${item.id}`}
                        />
                        <div>ID: {item.id}</div>
                        <button
                          onClick={() => handleDeleteAvatar(item.id)}
                          disabled={isUploading}
                          style={{
                            backgroundColor: 'red',
                            color: 'white',
                            padding: '5px',
                            marginTop: '5px',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isUploading ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
            <div className="pagination">
              {showPrevButton && (
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={isUploading}
                >
                  Previous
                </button>
              )}
              {paginationButtons}
              {showNextButton && (
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={isUploading}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </IncludeSideBar>
  );
}

export default AvatarScreen;